/**
 * Schedule Progress Overrides — reads/writes through the in-memory store.
 *
 * Storage:  src/Data/store.ts (in-memory Maps)
 * Durable:  PostgreSQL via API write-through
 *
 * NO localStorage reads or writes happen in this file.
 */

import {
  batchUpsertScheduleSeriesPoints,
  type CreateScheduleSeriesPointRequest,
} from "../../api/scheduleSeriesPoints";
import {
  upsertCurveMeta,
  type CreateCurveMetaRequest,
} from "../../api/curveMeta";
import {
  scheduleSeriesStore,
  scheduleMetaStore,
  scheduleLocalWriteTs,
  storeKey,
  isScheduleHydrated,
} from "../store";
import { broadcastScheduleUpdate } from "../../api/crossTabSync";

// ── Types ────────────────────────────────────────────────────

export type ScheduleProgressPoint = {
  project_guid: string;
  instance_guid?: string;
  reporting_date: string; // YYYY-MM-DD
  baseline_complete: number | null; // 0..100 (null = not set)
  actual_complete: number | null; // 0..100 (null = no data)
  forecast_complete: number | null; // 0..100 (null = not set)
  /**
   * DEV-851: Submission timestamp from the API (when the row was actually
   * entered, distinct from reporting_date which says what period it covers).
   * Used by DataReliabilityTab to compute per-row submission lateness
   * matching the engine's Timeliness rule (`created_at - reporting_date > grace`).
   */
  created_at?: string;
};

export type ScheduleCurveType = "linear" | "front_loaded" | "back_loaded" | "sigmoidal" | "custom" | "copy" | "manual";
export type ProgressDirection = "increasing" | "decreasing";

export type ReportingCadence = "daily" | "weekly" | "monthly" | "quarterly";

export type ReportingSchedule = {
  cadence: ReportingCadence;
  interval?: number;
  by_weekday?: number[];
  due_time_local?: string;
  timezone?: string;
  until_date?: string;
};

export type ScheduleCurveMeta = {
  project_guid?: string;
  instance_guid?: string;
  curve_type: ScheduleCurveType;
  source_project_guid?: string;
  direction?: ProgressDirection;
  reporting_schedule?: ReportingSchedule;
  baseline_draft_id?: string;
  baseline_published_id?: string;
  baseline_published_version?: number;
  baseline_start_date?: string;
  baseline_end_date?: string;
  baseline_start_date_override?: string;
  baseline_end_date_override?: string;
  suppress_seed?: boolean;
  cleared_at?: string;
  /**
   * DEV-963: WBS code attached to the curve meta. Used by
   * ScheduleThermometer for breakdown reporting. Optional because the
   * API may not return it for older rows and the field is purely
   * informational (not used for schedule math).
   */
  wbs_code?: string;
  /**
   * DEV-963: Optional role-slot GUID. Same rationale as wbs_code —
   * informational only.
   */
  assigned_role_slot_guid?: string;
};

// ── Internal helpers ─────────────────────────────────────────

function sortSeries(rows: ScheduleProgressPoint[]) {
  return [...rows].sort((a, b) => a.reporting_date.localeCompare(b.reporting_date));
}

function isISODate(s: any): boolean {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function numOrNull(v: any): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Ensure store is warm (one-time localStorage migration if API hasn't loaded). */
function ensureWarm(): void {
  // No-op: localStorage warm-start removed. Data comes from API hydration only.
}

// ── Series reads ─────────────────────────────────────────────

export function getScheduleProgressOverrideSeries(projectGuid: string): ScheduleProgressPoint[] {
  return getScheduleProgressOverrideSeriesForInstance(projectGuid, undefined);
}

export function getScheduleProgressOverrideSeriesForInstance(
  projectGuid: string,
  instanceGuid?: string | null,
): ScheduleProgressPoint[] {
  ensureWarm();
  const k = storeKey(projectGuid, instanceGuid);
  const data = scheduleSeriesStore.get(k);
  if (Array.isArray(data) && data.length) return sortSeries(data);

  // Fallback: if instance-specific key not found, try project-level key.
  // This handles cases where series data was written with a different
  // (or null) instance_guid than what kpi_config specifies.
  if (instanceGuid) {
    const projKey = storeKey(projectGuid, undefined);
    const projData = scheduleSeriesStore.get(projKey);
    if (Array.isArray(projData) && projData.length) return sortSeries(projData);
  }

  return [];
}

// ── Series writes ────────────────────────────────────────────

export function setScheduleProgressOverrideSeries(projectGuid: string, rows: ScheduleProgressPoint[]) {
  setScheduleProgressOverrideSeriesForInstance(projectGuid, undefined, rows);
}

export function setScheduleProgressOverrideSeriesForInstance(
  projectGuid: string,
  instanceGuid: string | null | undefined,
  rows: ScheduleProgressPoint[],
  options?: { skipApiWrite?: boolean },
) {
  const p = String(projectGuid || "").trim();
  const k = storeKey(p, instanceGuid);
  const cleaned = sortSeries(
    (rows || [])
      .map((r) => ({
        project_guid: p,
        instance_guid: instanceGuid ? String(instanceGuid) : undefined,
        reporting_date: String((r as any).reporting_date || "").trim(),
        baseline_complete: numOrNull((r as any).baseline_complete),
        actual_complete: numOrNull((r as any).actual_complete),
        forecast_complete: numOrNull((r as any).forecast_complete),
      }))
      .filter((r) => isISODate(r.reporting_date))
  );

  // Update in-memory store + stamp local write time so hydration won't
  // overwrite this data during the grace window.
  scheduleSeriesStore.set(k, cleaned);
  scheduleLocalWriteTs.set(k, Date.now());

  // Broadcast to other browser tabs via BroadcastChannel (instant, no API round-trip).
  broadcastScheduleUpdate(p, instanceGuid ? String(instanceGuid) : undefined, cleaned);

  // Signal React to re-render.
  try { window.dispatchEvent(new CustomEvent("l4e_schedule_progress_changed")); } catch { /* ignore */ }

  // Write-through to API (fire-and-forget) — skip when caller handles API call.
  if (!options?.skipApiWrite && cleaned.length > 0) {
    const apiPoints: CreateScheduleSeriesPointRequest[] = cleaned.map((r) => ({
      projectId: r.project_guid,
      instanceGuid: r.instance_guid || undefined,
      reportingDate: r.reporting_date,
      baselineComplete: r.baseline_complete ?? undefined,
      actualComplete: r.actual_complete ?? undefined,
      forecastComplete: r.forecast_complete ?? undefined,
    }));
    batchUpsertScheduleSeriesPoints(apiPoints).catch((err) => {
      console.warn("[scheduleProgressOverrides] API write-through failed:", err?.message || err);
    });
  }
}

// ── Series clear ─────────────────────────────────────────────

export function clearScheduleProgressOverrideSeries(projectGuid: string) {
  clearScheduleProgressOverrideSeriesForInstance(projectGuid, undefined);
}

export function clearScheduleProgressOverrideSeriesForInstance(
  projectGuid: string,
  instanceGuid?: string | null,
) {
  const k = storeKey(projectGuid, instanceGuid);
  scheduleSeriesStore.delete(k);
  try { window.dispatchEvent(new CustomEvent("l4e_schedule_progress_changed")); } catch { /* ignore */ }
}

// ── Curve metadata reads ─────────────────────────────────────

export function getScheduleCurveMeta(projectGuid: string): ScheduleCurveMeta | null {
  return getScheduleCurveMetaForInstance(projectGuid, undefined);
}

export function getScheduleCurveMetaForInstance(
  projectGuid: string,
  instanceGuid?: string | null,
): ScheduleCurveMeta | null {
  ensureWarm();
  const k = storeKey(projectGuid, instanceGuid);
  return scheduleMetaStore.get(k) || null;
}

// ── Curve metadata writes ────────────────────────────────────

export function setScheduleCurveMeta(projectGuid: string, meta: ScheduleCurveMeta) {
  setScheduleCurveMetaForInstance(projectGuid, undefined, meta);
}

export function setScheduleCurveMetaForInstance(
  projectGuid: string,
  instanceGuid: string | null | undefined,
  meta: ScheduleCurveMeta,
) {
  const p = String(projectGuid || "").trim();
  const k = storeKey(p, instanceGuid);

  const cleaned: ScheduleCurveMeta = {
    project_guid: p,
    instance_guid: instanceGuid ? String(instanceGuid) : undefined,
    curve_type: meta.curve_type,
    source_project_guid: meta.source_project_guid ? String(meta.source_project_guid) : undefined,
    direction: meta.direction === "decreasing" ? "decreasing" : "increasing",
    reporting_schedule: meta.reporting_schedule
      ? {
          cadence: meta.reporting_schedule.cadence,
          interval:
            typeof (meta.reporting_schedule as any).interval === "number" && Number.isFinite((meta.reporting_schedule as any).interval)
              ? (meta.reporting_schedule as any).interval
              : undefined,
          by_weekday: Array.isArray((meta.reporting_schedule as any).by_weekday)
            ? (meta.reporting_schedule as any).by_weekday.map((x: any) => Number(x)).filter((x: any) => Number.isFinite(x))
            : undefined,
          due_time_local: (meta.reporting_schedule as any).due_time_local ? String((meta.reporting_schedule as any).due_time_local) : undefined,
          timezone: (meta.reporting_schedule as any).timezone ? String((meta.reporting_schedule as any).timezone) : undefined,
          until_date: (meta.reporting_schedule as any).until_date ? String((meta.reporting_schedule as any).until_date) : undefined,
        }
      : undefined,
    baseline_draft_id: meta.baseline_draft_id ? String(meta.baseline_draft_id) : undefined,
    baseline_published_id: meta.baseline_published_id ? String(meta.baseline_published_id) : undefined,
    baseline_published_version:
      typeof meta.baseline_published_version === "number" && Number.isFinite(meta.baseline_published_version)
        ? meta.baseline_published_version
        : undefined,
    baseline_start_date: meta.baseline_start_date ? String(meta.baseline_start_date) : undefined,
    baseline_end_date: meta.baseline_end_date ? String(meta.baseline_end_date) : undefined,
    baseline_start_date_override: meta.baseline_start_date_override ? String(meta.baseline_start_date_override) : undefined,
    baseline_end_date_override: meta.baseline_end_date_override ? String(meta.baseline_end_date_override) : undefined,
    suppress_seed: !!(meta as any).suppress_seed,
    cleared_at: (meta as any).cleared_at ? String((meta as any).cleared_at) : undefined,
  };

  // Update in-memory store.
  scheduleMetaStore.set(k, cleaned);

  // Signal React.
  try { window.dispatchEvent(new CustomEvent("l4e_schedule_progress_changed")); } catch { /* ignore */ }

  // Write-through to API.
  const metaPayload: CreateCurveMetaRequest = {
    projectId: p,
    instanceGuid: instanceGuid ? String(instanceGuid) : undefined,
    category: "schedule",
    metaJson: cleaned,
  };
  upsertCurveMeta(metaPayload).catch((err) => {
    console.warn("[scheduleProgressOverrides] Curve meta API write-through failed:", err?.message || err);
  });
}

// ── Curve metadata clear ─────────────────────────────────────

export function clearScheduleCurveMeta(projectGuid: string) {
  clearScheduleCurveMetaForInstance(projectGuid, undefined);
}

export function clearScheduleCurveMetaForInstance(
  projectGuid: string,
  instanceGuid?: string | null,
) {
  const k = storeKey(projectGuid, instanceGuid);
  scheduleMetaStore.delete(k);
  try { window.dispatchEvent(new CustomEvent("l4e_schedule_progress_changed")); } catch { /* ignore */ }
}

// ── Migration helper (legacy) ────────────────────────────────
// Kept for backward compatibility but operates on the store, not localStorage.

export function migrateLegacyScheduleProgressToInstance(projectGuid: string, instanceGuid: string) {
  const p = String(projectGuid || "").trim();
  const i = String(instanceGuid || "").trim();
  if (!p || !i) return;

  // Series: copy project-level → instance-level
  const projKey = storeKey(p);
  const instKey = storeKey(p, i);
  const projSeries = scheduleSeriesStore.get(projKey);
  if (Array.isArray(projSeries) && projSeries.length && !scheduleSeriesStore.has(instKey)) {
    scheduleSeriesStore.set(instKey, projSeries.map((r) => ({ ...r, instance_guid: i })));
    scheduleSeriesStore.delete(projKey);
  }

  // Meta: same pattern
  const projMeta = scheduleMetaStore.get(projKey);
  if (projMeta && !scheduleMetaStore.has(instKey)) {
    scheduleMetaStore.set(instKey, { ...projMeta, project_guid: p, instance_guid: i });
    scheduleMetaStore.delete(projKey);
  }
}
